import '../../assets/styles/login.scss';
import '../../assets/styles/playground.scss';
import PlaygroundTopHeader from '../../components/playground-v2/PlaygroundTopHeader';
import PlaygroundEventBannerPopup from '../../components/playground-v2/PlaygroundEventBannerPopup';
import PlaygroundV2RenewalPopup, { canShowRenewalPopup } from '../../components/playground-v2/PlaygroundV2RenewalPopup';
import PlaygroundPointCard from '../../components/playground-v2/PlaygroundPointCard';
import PlaygroundRouteCard from '../../components/playground-v2/PlaygroundRouteCard';
import PlaygroundMissionCard from '../../components/playground-v2/PlaygroundMissionCard';
import PlaygroundMiniGamesCardRow from '../../components/playground-v2/PlaygroundMiniGamesCardRow';
import PlaygroundStackingCardList from '../../components/playground-v2/PlaygroundStackingCardList';
import PlaygroundQuickRewardRow from '../../components/playground-v2/PlaygroundQuickRewardRow';
import BuzzvilAd from '../../components/BuzzvilAd';
import PointImg from '../../assets/images/img_point.png';
import { ReactComponent as VectorWave } from '../../assets/icons/vector-wave.svg';
import { ReactComponent as VectorWaveBlack } from '../../assets/icons/vector-wave-black.svg';
import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { isDevelopmentMode, useRunTimeEnvironment } from '../../utils/hooks';
import useSWR from 'swr';
import ModalDialog from '../../components/common/Modal';
import { TextField } from '../../components/common/Input';
import Button from '../../components/common/Button';
import ToastAlert from '../../components/common/Toast';

const PLAYGROUND_SCROLL_KEY = 'playground-scroll-top';
const ALERT_TIMEOUT = 2500;

function PlaygroundPage() {
  const RTE = useRunTimeEnvironment();
  const { API_SERVER_DOMAIN, useAccessToken, fetchWithToken, appInfo } = useContext(AuthContext);
  const accessToken = useAccessToken();
  const [adParams, setAdParams] = useState({});
  const [friendCode, setFriendCode] = useState('');
  const [friendCodeOpen, setFriendCodeOpen] = useState(true);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState(false);
  const { data: authProfile, isLoading: isAuthProfileLoading } = useSWR(
    accessToken && [`${API_SERVER_DOMAIN}/auth/user/check`, accessToken],
    fetchWithToken,
  );
  const { data: profileInfo } = useSWR(
    accessToken && [`${API_SERVER_DOMAIN}/user/profile/info`, accessToken],
    fetchWithToken,
  );

  const openOfferwallMain = () => {
    if (RTE === 'APP') {
      window.flutter_inappwebview?.callHandler?.('goto_offerwall_main');
    }
  };

  const getReward = async () => {
    if (!authProfile?.userInfo) return;

    if (authProfile.refCode === friendCode) {
      alert('스스로 초대 할 수 없어요.');
      return;
    }

    const response = await fetch(`${API_SERVER_DOMAIN}/user/ref/submit`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ref_code: friendCode,
      }),
    });

    const jsonData = await response.json();
    if (jsonData.result === 'ok') {
      setAlertMessage(`소환 코드를 통해 가입하신 ${authProfile.userInfo.nickName}님께 200포인트가 지급 되었어요.`);
    } else {
      setAlertMessage(jsonData.message);
    }
    setFriendCodeOpen(false);

    setTimeout(() => {
      setWelcomeOpen(true);
    }, ALERT_TIMEOUT - 300);
  };

  useEffect(() => {
    if (RTE !== 'APP') return;
    const userNo = authProfile?.userInfo?.userNo;
    if (!accessToken || !userNo) return;
    window.flutter_inappwebview?.callHandler?.('grantAuthInfo', accessToken, userNo);
  }, [RTE, accessToken, authProfile?.userInfo?.userNo]);

  useEffect(() => {
    const getNativeInfo = async () => {
      const info = await window.flutter_inappwebview?.callHandler?.('get_native_play');
      if (!info) return;
      setAdParams({
        platform: appInfo?.os?.substring(0, 1).toUpperCase(),
        unitId: info.unit_id,
        ifa: info.ifa,
        ifv: info.ifv,
      });
    };

    if (RTE === 'APP') {
      getNativeInfo();
    }

    const pageContainer = document.getElementsByClassName('page-container')[0];
    if (!pageContainer) return;

    const saveScroll = () => {
      const top = Math.max(pageContainer.scrollTop ?? 0, window.scrollY || 0);
      sessionStorage.setItem(PLAYGROUND_SCROLL_KEY, String(top));
    };

    pageContainer.addEventListener('scroll', saveScroll, { passive: true });

    return () => {
      pageContainer.removeEventListener('scroll', saveScroll);
    };
  }, [RTE, appInfo?.os]);

  useEffect(() => {
    const pageContainer = document.getElementsByClassName('page-container')[0];
    if (!pageContainer) return;

    const raw = sessionStorage.getItem(PLAYGROUND_SCROLL_KEY);
    if (!raw) return;

    const targetTop = Number(raw);
    if (!Number.isFinite(targetTop) || targetTop <= 0) return;

    let tries = 0;
    const restore = () => {
      pageContainer.scrollTo(0, targetTop);
      window.scrollTo(0, targetTop);
      const restored = Math.abs(pageContainer.scrollTop - targetTop) < 2;
      if (!restored && tries < 20) {
        tries += 1;
        requestAnimationFrame(restore);
      }
    };

    requestAnimationFrame(restore);
  }, []);

  const showRenewalPopup =
    Boolean(authProfile?.userInfo) &&
    !isAuthProfileLoading &&
    !(authProfile.isFirstLogin && friendCodeOpen) &&
    !welcomeOpen &&
    canShowRenewalPopup();

  const showEventBanner =
    Boolean(authProfile?.userInfo) &&
    !isAuthProfileLoading &&
    !friendCodeOpen &&
    !welcomeOpen &&
    !showRenewalPopup;

  return (
    <main className="flex min-h-screen w-full flex-col items-start justify-start bg-black">
      <PlaygroundV2RenewalPopup enabled={showRenewalPopup} />
      <PlaygroundEventBannerPopup apiDomain={API_SERVER_DOMAIN} enabled={showEventBanner} />

      <section className="w-full">
        <div className="w-full bg-gradient-to-t from-[#EAE2FF] via-[#7281DC] to-[#EAE2FF]">
          <PlaygroundTopHeader />
          <PlaygroundPointCard point={profileInfo?.info?.point ?? 0} />
          <div data-svg-wrapper className="w-full mt-9">
            <VectorWave className="block h-7 w-full" preserveAspectRatio="none" />
          </div>
        </div>
        <div className="bg-gray01 pb-7 px-2">
          <PlaygroundRouteCard />
          <PlaygroundMissionCard onOpenOfferwall={openOfferwallMain} />
        </div>
      </section>

      <section className="w-full bg-black">
        <div data-svg-wrapper className="relative h-3 w-full">
          <VectorWaveBlack className="absolute left-0 -top-5 block h-10 w-full" preserveAspectRatio="none" />
        </div>
        <div className="relative z-10 px-4 mb-3 self-stretch justify-center text-gray02 text-sm font-extrabold font-['Pretendard']">
          한방을 노려라 !
        </div>

        <div className="px-2">
          <PlaygroundMiniGamesCardRow />
          {adParams?.unitId ? (
            <div className="mt-5 rounded-[10px] bg-white py-2">
              <BuzzvilAd adParams={adParams} extraStyle="buzzilad-container-playground" />
            </div>
          ) : (
            <div className="w-full h-36 mt-5 bg-gray02 rounded-[10px]" />
          )}
        </div>
      </section>

      <section className="w-full bg-black py-5">
        <div className="relative z-10 px-4 mb-3 self-stretch justify-center text-gray02 text-sm font-extrabold font-['Pretendard']">
          차곡차곡 모으기
        </div>

        <PlaygroundStackingCardList />

        <div className="relative w-full h-8 py-5">
          <div className="absolute inset-x-0 top-1/2 h-[2px] -translate-y-1/2 bg-black/90" />
          <div className="absolute left-6 right-6 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-white/75 to-transparent" />
        </div>
        <PlaygroundQuickRewardRow />
      </section>

      <ToastAlert show={!!alertMessage} onClose={setAlertMessage} text={alertMessage} timeout={ALERT_TIMEOUT} />

      {authProfile?.userInfo && (
        <>
          <ModalDialog
            title="나를 소환한 친구 코드"
            className="friend-code-modal"
            open={authProfile.isFirstLogin && friendCodeOpen}
            onCancel={() => setFriendCodeOpen(false)}
          >
            <TextField onChange={setFriendCode} defaultValue="" />
            <p className="text-light">초대받은 후 1달 내 회원탈퇴를 하시면 초대로 받으신 포인트가 모두 사라져요.</p>
            <div className="friend-nickname">
              <span>부여받은 닉네임</span>
              <span className="text-accent">{authProfile.userInfo.nickName}</span>
            </div>
            <Button onClick={() => getReward()}>확인</Button>
          </ModalDialog>

          <ModalDialog title="가입을 축하합니다!" className="welcome-modal" open={welcomeOpen}>
            <p className="text-light">
              가입 선물로 <strong>300p</strong>
              <br />
              드려요!!
            </p>
            <img src={PointImg} width={124} role="presentation" alt="" />
            <Button onClick={() => setWelcomeOpen(false)}>확인</Button>
          </ModalDialog>
        </>
      )}

    </main>
  );
}

export default PlaygroundPage;
